/**
 * redisCache.js
 * Redis caching layer for EmpireX high-traffic data.
 *
 * Architecture:
 * - In browser: uses in-memory Map with TTL (simulates Redis SETEX behavior)
 * - Server-side (Node.js tick worker): connects to actual Redis via REDIS_HOST/PORT/PASSWORD
 * - Graceful fallback: if Redis unavailable, falls through to Supabase directly
 *
 * Cache Keys:
 *   leaderboard:net_worth   -> 30s TTL
 *   economy_state:current   -> 30s TTL
 *   stocks:prices           -> 30s TTL
 */

import { supabase } from '../lib/supabase';

// Cache Statistics
const cacheStats = {
  hits: 0,
  misses: 0,
  evictions: 0,
  totalQueryTimeWithCache: 0,
  totalQueryTimeWithoutCache: 0,
  queriesWithCache: 0,
  queriesWithoutCache: 0,
  keyAccessCount: {},
  invalidations: 0,
  cacheVersion: 1,
};

// In-Memory Cache Store (Browser-compatible Redis simulation)
const memoryCache = new Map();

function memGet(key) {
  const entry = memoryCache?.get(key);
  if (!entry) return null;
  if (Date.now() > entry?.expiresAt) {
    memoryCache?.delete(key);
    cacheStats.evictions++;
    return null;
  }
  // TTL refresh on access for hot data (extend by 20% of original TTL)
  if (entry?.ttlSeconds && entry?.expiresAt - Date.now() < entry?.ttlSeconds * 200) {
    entry.expiresAt = Date.now() + entry?.ttlSeconds * 1000;
  }
  return entry?.value;
}

function memSet(key, value, ttlSeconds) {
  memoryCache?.set(key, {
    value,
    expiresAt: Date.now() + ttlSeconds * 1000,
    createdAt: Date.now(),
    ttlSeconds,
    version: cacheStats?.cacheVersion,
  });
  // Cache size monitoring — warn if > 500 entries
  if (memoryCache?.size > 500) {
    console.warn(`[Cache] Size alert: ${memoryCache?.size} entries in memory cache`);
  }
}

function memDel(pattern) {
  const prefix = pattern?.replace(/\*/g, '');
  let deleted = 0;
  for (const key of memoryCache?.keys()) {
    if (key?.startsWith(prefix)) {
      memoryCache?.delete(key);
      deleted++;
    }
  }
  return deleted;
}

function trackKeyAccess(key) {
  cacheStats.keyAccessCount[key] = (cacheStats?.keyAccessCount?.[key] || 0) + 1;
}

// Redis Connection Status
const redisConnectionStatus = {
  connected: false,
  mode: typeof window !== 'undefined' ? 'memory' : 'redis',
  host: typeof window !== 'undefined' ? null : (typeof process !== 'undefined' ? process?.env?.REDIS_HOST || 'localhost' : null),
  port: typeof window !== 'undefined' ? null : (typeof process !== 'undefined' ? parseInt(process?.env?.REDIS_PORT || '6379') : null),
  lastHealthCheck: null,
  error: null,
};

export function getCacheHealth() {
  const hitRate = cacheStats?.hits + cacheStats?.misses > 0
    ? ((cacheStats?.hits / (cacheStats?.hits + cacheStats?.misses)) * 100)?.toFixed(1)
    : '0.0';

  const avgTimeWithCache = cacheStats?.queriesWithCache > 0
    ? (cacheStats?.totalQueryTimeWithCache / cacheStats?.queriesWithCache)?.toFixed(1)
    : '0';

  const avgTimeWithoutCache = cacheStats?.queriesWithoutCache > 0
    ? (cacheStats?.totalQueryTimeWithoutCache / cacheStats?.queriesWithoutCache)?.toFixed(1)
    : '0';

  const topKeys = Object.entries(cacheStats?.keyAccessCount)?.sort(([, a], [, b]) => b - a)?.slice(0, 5)?.map(([key, count]) => ({ key, count }));

  return {
    connected: redisConnectionStatus?.connected,
    mode: redisConnectionStatus?.mode,
    host: redisConnectionStatus?.host,
    port: redisConnectionStatus?.port,
    error: redisConnectionStatus?.error,
    lastHealthCheck: redisConnectionStatus?.lastHealthCheck,
    stats: {
      hits: cacheStats?.hits,
      misses: cacheStats?.misses,
      evictions: cacheStats?.evictions,
      invalidations: cacheStats?.invalidations,
      hitRate: parseFloat(hitRate),
      avgQueryTimeWithCache: parseFloat(avgTimeWithCache),
      avgQueryTimeWithoutCache: parseFloat(avgTimeWithoutCache),
      cacheSize: memoryCache?.size,
      cacheVersion: cacheStats?.cacheVersion,
      topKeys,
    },
  };
}

export function resetCacheStats() {
  cacheStats.hits = 0;
  cacheStats.misses = 0;
  cacheStats.evictions = 0;
  cacheStats.totalQueryTimeWithCache = 0;
  cacheStats.totalQueryTimeWithoutCache = 0;
  cacheStats.queriesWithCache = 0;
  cacheStats.queriesWithoutCache = 0;
  cacheStats.keyAccessCount = {};
}

const TTL = {
  LEADERBOARD: 30,
  ECONOMY_STATE: 30,
  STOCK_PRICES: 30,
};

// Leaderboard Cache
const LEADERBOARD_KEY = 'leaderboard:net_worth';

export async function getLeaderboard() {
  const cacheKey = LEADERBOARD_KEY;
  trackKeyAccess(cacheKey);

  const cached = memGet(cacheKey);
  if (cached) {
    cacheStats.hits++;
    cacheStats.queriesWithCache++;
    return { data: cached, fromCache: true };
  }

  cacheStats.misses++;
  const t0 = performance.now();

  try {
    let result = await supabase
      ?.from('leaderboard_net_worth')
      ?.select('user_id, username, net_worth, level, trading_skill, total_trades')
      ?.order('rank', { ascending: true })
      ?.limit(50);

    let data = result?.data;

    if (result?.error || !data?.length) {
      const fallback = await supabase
        ?.from('player_stats')
        ?.select('user_id, net_worth, level, xp, user_profiles(username)')
        ?.order('net_worth', { ascending: false })
        ?.limit(50);
      data = fallback?.data;
      if (fallback?.error) throw fallback?.error;
    }

    const mapped = (data || [])?.map((p, i) => ({
      rank: p?.rank || i + 1,
      username: p?.username || p?.user_profiles?.username || 'Unknown',
      netWorth: parseFloat(p?.net_worth || 0),
      level: p?.level || 1,
      faction: 'None',
      change: 0,
      badge: i === 0 ? '\uD83D\uDC51' : i === 1 ? '\uD83E\uDD48' : i === 2 ? '\uD83E\uDD49' : null,
    }));

    memSet(cacheKey, mapped, TTL?.LEADERBOARD);

    const elapsed = performance.now() - t0;
    cacheStats.totalQueryTimeWithoutCache += elapsed;
    cacheStats.queriesWithoutCache++;

    return { data: mapped, fromCache: false };
  } catch (err) {
    console.error('[Cache] getLeaderboard error:', err);
    return { data: [], fromCache: false, error: err?.message };
  }
}

export function invalidateLeaderboard() {
  let deleted = memDel('leaderboard:*');
  cacheStats.invalidations += deleted;
  console.debug('[Cache] Invalidated leaderboard (' + deleted + ' keys)');
}

// Economy State Cache
const ECONOMY_STATE_KEY = 'economy_state:current';

export async function getEconomyState() {
  const cacheKey = ECONOMY_STATE_KEY;
  trackKeyAccess(cacheKey);

  const cached = memGet(cacheKey);
  if (cached) {
    cacheStats.hits++;
    cacheStats.queriesWithCache++;
    return { data: cached, fromCache: true };
  }

  cacheStats.misses++;
  const t0 = performance.now();

  try {
    const { data, error } = await supabase
      ?.from('economy_state')
      ?.select('*')
      ?.limit(1)
      ?.maybeSingle();

    if (error) throw error;
    if (data) memSet(cacheKey, data, TTL?.ECONOMY_STATE);

    const elapsed = performance.now() - t0;
    cacheStats.totalQueryTimeWithoutCache += elapsed;
    cacheStats.queriesWithoutCache++;

    return { data, fromCache: false };
  } catch (err) {
    console.error('[Cache] getEconomyState error:', err);
    return { data: null, fromCache: false, error: err?.message };
  }
}

export function invalidateEconomyState() {
  let deleted = memDel('economy_state:*');
  cacheStats.invalidations += deleted;
  console.debug('[Cache] Invalidated economy state (' + deleted + ' keys)');
}

// Stock Prices Cache
const STOCK_PRICES_KEY = 'stocks:prices';

export async function getStockPrices() {
  const cacheKey = STOCK_PRICES_KEY;
  trackKeyAccess(cacheKey);

  const cached = memGet(cacheKey);
  if (cached) {
    cacheStats.hits++;
    cacheStats.queriesWithCache++;
    return { data: cached, fromCache: true };
  }

  cacheStats.misses++;
  const t0 = performance.now();

  try {
    const { data, error } = await supabase
      ?.from('stocks')
      ?.select('id, ticker, company_name, sector, current_price, open_price, high_price, low_price, close_price, volume, market_cap, volatility_factor, dividend_yield, is_listed')
      ?.eq('is_listed', true)
      ?.order('market_cap', { ascending: false });

    if (error) throw error;
    if (data) memSet(cacheKey, data, TTL?.STOCK_PRICES);

    const elapsed = performance.now() - t0;
    cacheStats.totalQueryTimeWithoutCache += elapsed;
    cacheStats.queriesWithoutCache++;

    return { data: data || [], fromCache: false };
  } catch (err) {
    console.error('[Cache] getStockPrices error:', err);
    return { data: [], fromCache: false, error: err?.message };
  }
}

export function invalidateStockPrices() {
  let deleted = memDel('stocks:*');
  cacheStats.invalidations += deleted;
  console.debug('[Cache] Invalidated stock prices (' + deleted + ' keys)');
}

export function getCacheEntry(key) {
  return memoryCache?.get(key) || null;
}

export function listCacheKeys() {
  const now = Date.now();
  const keys = [];
  for (const [key, entry] of memoryCache?.entries()) {
    const ttlRemaining = Math.max(0, Math.floor((entry?.expiresAt - now) / 1000));
    if (ttlRemaining > 0) {
      keys?.push({ key, ttlRemaining, accessCount: cacheStats?.keyAccessCount?.[key] || 0 });
    }
  }
  return keys;
}

/**
 * Invalidate net worth snapshot cache when player_stats updated.
 * Also clears leaderboard since net worth ranking may have changed.
 */
export function invalidateNetWorthCache() {
  let deleted = memDel('net_worth:*');
  deleted += memDel('leaderboard:*');
  cacheStats.invalidations += deleted;
  console.debug('[Cache] Invalidated net worth + leaderboard (' + deleted + ' keys)');
}

/**
 * Invalidate faction vault cache when vault transactions occur.
 */
export function invalidateFactionVaultCache(factionId) {
  const prefix = factionId ? `faction_vault:${factionId}` : 'faction_vault:';
  let deleted = memDel(prefix);
  deleted += memDel('faction:*');
  cacheStats.invalidations += deleted;
  console.debug('[Cache] Invalidated faction vault (' + deleted + ' keys)');
}

/**
 * Invalidate player stats cache for a specific user.
 */
export function invalidatePlayerStatsCache(userId) {
  const prefix = userId ? `player_stats:${userId}` : 'player_stats:';
  let deleted = memDel(prefix);
  cacheStats.invalidations += deleted;
  console.debug('[Cache] Invalidated player stats (' + deleted + ' keys)');
}

/**
 * Bump cache version — forces all stale entries to be treated as misses.
 * Use when a schema migration or major data change occurs.
 */
export function bumpCacheVersion() {
  cacheStats.cacheVersion += 1;
  // Evict all entries from previous version
  let evicted = 0;
  for (const [key, entry] of memoryCache?.entries()) {
    if (entry?.version !== cacheStats?.cacheVersion) {
      memoryCache?.delete(key);
      evicted++;
    }
  }
  cacheStats.evictions += evicted;
  console.debug(`[Cache] Version bumped to ${cacheStats?.cacheVersion}, evicted ${evicted} stale entries`);
  return cacheStats?.cacheVersion;
}

/**
 * Cache warming — pre-load frequently accessed data on Dashboard mount.
 * Fetches leaderboard, economy state, and stock prices in parallel.
 */
export async function warmDashboardCache() {
  const tasks = [];
  if (!memGet(LEADERBOARD_KEY)) tasks?.push(getLeaderboard());
  if (!memGet(ECONOMY_STATE_KEY)) tasks?.push(getEconomyState());
  if (!memGet(STOCK_PRICES_KEY)) tasks?.push(getStockPrices());
  if (tasks?.length > 0) {
    try {
      await Promise.allSettled(tasks);
      console.debug(`[Cache] Warmed ${tasks?.length} cache entries`);
    } catch (err) {
      console.warn('[Cache] warmDashboardCache error:', err);
    }
  }
}

/**
 * Lazy cache population — fetch from DB on miss, store for subsequent requests.
 * Generic helper for any cache key.
 */
export async function lazyGet(key, fetchFn, ttlSeconds = 30) {
  trackKeyAccess(key);
  const cached = memGet(key);
  if (cached) {
    cacheStats.hits++;
    return { data: cached, fromCache: true };
  }
  cacheStats.misses++;
  try {
    let data = await fetchFn();
    if (data != null) memSet(key, data, ttlSeconds);
    return { data, fromCache: false };
  } catch (err) {
    console.error(`[Cache] lazyGet(${key}) error:`, err);
    return { data: null, fromCache: false, error: err?.message };
  }
}

/**
 * Flush all cache entries — called on logout to clear stale data.
 */
export function flushCache() {
  memoryCache?.clear();
  resetCacheStats();
  console.debug('[Cache] All cache entries flushed');
}

export default {
  getLeaderboard,
  getEconomyState,
  getStockPrices,
  invalidateLeaderboard,
  invalidateEconomyState,
  invalidateStockPrices,
  invalidateNetWorthCache,
  invalidateFactionVaultCache,
  invalidatePlayerStatsCache,
  bumpCacheVersion,
  warmDashboardCache,
  lazyGet,
  getCacheHealth,
  resetCacheStats,
  listCacheKeys,
  flushCache,
};
